import { EMAIL_PREFERENCES_TOKEN_PARAM, isEmailPreferencesPath } from "./emailPreferencesRoute";

/** Keeps the token for the tab, so a reload after it left the address bar still opens the page. */
const TOKEN_STORAGE_KEY = "emailPreferences.token";

let capturedToken: string | null = null;

// Blocked storage (private mode, a locked-down webview) must not stop the app from starting; the
// page then works for this load from the in-memory token alone.
const readStoredToken = () => {
  try {
    capturedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    console.warn("Email preferences token storage unavailable");
  }
};

const keepToken = (token: string) => {
  capturedToken = token;
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    console.warn("Email preferences token storage unavailable");
  }
};

/**
 * Keeps the token `url` carries and returns `url` without it, or `null` when it carries none.
 */
export const takeEmailPreferencesToken = (url: URL): URL | null => {
  const fromLink = url.searchParams.get(EMAIL_PREFERENCES_TOKEN_PARAM);
  if (fromLink === null) return null;

  keepToken(fromLink);
  const withoutToken = new URL(url);
  withoutToken.searchParams.delete(EMAIL_PREFERENCES_TOKEN_PARAM);
  return withoutToken;
};

/**
 * The token is a long-lived bearer for the patient's preferences, so it leaves the address bar at
 * startup, before Datadog, Segment, Customer.io, AppsFlyer or the router read the URL. Must run
 * before any of them start. Off the email-preferences page this does nothing.
 */
export const captureEmailPreferencesToken = () => {
  const url = new URL(window.location.href);
  if (!isEmailPreferencesPath(url.pathname)) return;

  const withoutToken = takeEmailPreferencesToken(url);
  if (withoutToken === null) {
    readStoredToken();
    return;
  }

  window.history.replaceState(window.history.state, "", withoutToken);
};

/** The token `captureEmailPreferencesToken` took from the link, or `null` when it had none. */
export const readEmailPreferencesToken = (): string | null => capturedToken;
